// src/components/SoundTile.js
//
// One ambient sound (or personal track) with a play/pause toggle and a
// volume slider. Playing fades in via AudioEngine; pausing fades out.

import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import * as FileSystem from 'expo-file-system';
import Slider from '@react-native-community/slider';
import { Ionicons } from '@expo/vector-icons';
import { AudioEngine } from '../audio/AudioEngine';
import { Colors, Radius, Fonts, useFontScale } from '../theme';

/**
 * Returns true when `uri` is a local file:// path that can be checked with
 * FileSystem. Bundled asset URIs (numbers from require()) and remote http(s)
 * URLs are not checked — they can't go "missing" in the same way.
 */
function isLocalFileUri(uri) {
  return typeof uri === 'string' && uri.startsWith('file://');
}

export default function SoundTile({ id, name, uri, artworkUri, icon = 'musical-notes', onRemove, needsReimport = false, onReimport, onMissing, importing = false }) {
  // Initialise from the engine's live state so the tile accurately reflects
  // what's actually playing when the component mounts (or re-mounts after the
  // Info / onboarding screen unmounts the whole tab navigator).
  const _initialLayer = AudioEngine.getState().find((l) => l.id === id);
  const [playing, setPlaying] = useState(!!_initialLayer);
  const [volume, setVolume] = useState(_initialLayer?.volume ?? 0.7);
  const fontScale = useFontScale();

  // Keep the indicator in sync with the engine whenever audio state changes
  // externally (sleep timer stopping everything, interruptions, etc.).
  useEffect(() => {
    const unsub = AudioEngine.onChange((layers) => {
      const layer = layers.find((l) => l.id === id);
      setPlaying(!!layer);
      if (layer) setVolume(layer.volume);
    });
    return unsub;S
  }, [id]);

  const toggle = async () => {
    if (needsReimport) {
      // After a successful reimport the auto-play path calls setPlaying(true)
      // but the parent may not have re-rendered with needsReimport=false yet.
      // If audio is already playing (fade-in in progress), treat this tap as
      // a stop rather than a new reimport attempt, so the fade is cancelled
      // cleanly and no ghost audio layer is left behind.
      if (playing) {
        await AudioEngine.stopLayer(id);
        setPlaying(false);
        return;
      }

      // Delegate to parent which has access to reimportTrack from LibraryContext.
      // Await so that any rejection becomes a caught error rather than an
      // unhandled promise.  The warning clears automatically when the parent
      // re-renders with needsReimport=false after a successful reimport; on
      // cancel or failure the prop stays true so the tile remains in warning
      // state — no local state change is needed here.
      if (onReimport) {
        const updated = await onReimport().catch((err) => {
          console.warn('[SoundTile] reimport failed:', err);
          return null;
        });
        // If the parent returned the freshly imported track, auto-play it so
        // the user gets immediate confirmation that the file is good — matching
        // the behaviour of LibraryScreen's re-import flow.
        if (updated) {
          // Set playing=true optimistically before awaiting playLayer so that
          // React can batch this update with the parent's needsReimport=false
          // re-render.  Without this, React may flush the parent's re-render
          // while Audio.Sound.createAsync is still in flight, causing the slider
          // to appear visible-but-disabled until the file finishes loading.
          // Batching both state changes into the same render means the slider
          // arrives enabled the instant it becomes visible — no extra tap needed.
          setPlaying(true);
          try {
            await AudioEngine.playLayer(id, {
              uri: updated.uri,
              name: updated.name,
              volume,
              artworkUri: updated.artworkUri,
            });
          } catch (err) {
            console.warn('[SoundTile] auto-play after reimport failed:', err?.message ?? err);
            setPlaying(false);
          }
        }
      }
      return;
    }
    if (playing) {
      await AudioEngine.stopLayer(id);
      setPlaying(false);
    } else {
      // Before loading a local file, verify it still exists on disk. If it has
      // gone missing mid-session (e.g. a storage issue or re-install cleared
      // documents), surface the warning tile immediately instead of letting
      // AudioEngine fail silently.
      if (isLocalFileUri(uri)) {
        try {
          const info = await FileSystem.getInfoAsync(uri);
          if (!info.exists) {
            console.warn('[SoundTile] file missing at play time:', uri);
            // Await so the AsyncStorage write in markTrackMissing completes
            // before this function returns — prevents the flag being lost if
            // the component unmounts between the check and the persist.
            if (onMissing) await onMissing();
            return;
          }
        } catch (err) {
          // If the check itself throws (e.g. permission error), fall through
          // and let AudioEngine surface the real error rather than silently
          // swallowing it here.
          console.warn('[SoundTile] file existence check failed:', err);
        }
      }
      try {
        await AudioEngine.playLayer(id, { uri, name, volume, artworkUri });
        setPlaying(true);
      } catch (err) {
        // playLayer throws when the backing file can't be loaded (e.g. the
        // file has gone missing since the last session). Reset to stopped
        // state and surface the warning tile so the user can re-import.
        console.warn('[SoundTile] playLayer failed, marking layer as missing:', err?.message ?? err);
        setPlaying(false);
        // Await so the AsyncStorage write in markTrackMissing completes before
        // this catch block exits — prevents the flag being lost when the
        // component unmounts (e.g. a tab switch) between the throw and the persist.
        if (onMissing) await onMissing();
      }
    }
  };

  const onVolumeChange = (v) => {
    setVolume(v);
    if (playing) AudioEngine.setLayerVolume(id, v);
  };

  return (
    <View style={[styles.tile, playing && styles.tileActive, needsReimport && styles.tileMissing]}>
      {/* Left accent bar — only visible when playing */}
      <View style={[styles.accentBar, playing && styles.accentBarActive]} />

      {/* disabled={importing} prevents a second tap from starting a new reimport
          while the first is still in progress. The spinner is shown for the
          full duration — picker open, file copy, and AudioEngine hot-swap —
          and is guaranteed to clear via the finally block in the parent screen
          whether the user cancels, the call throws, or the import succeeds. */}
      <Pressable
        onPress={toggle}
        disabled={importing}
        style={[
          styles.iconButton,
          playing && styles.iconButtonActive,
          needsReimport && styles.iconButtonMissing,
          importing && styles.iconButtonImporting,
          // Scale the touch target with font size so it stays proportional at
          // large accessibility settings without clipping the surrounding tile.
          { width: Math.max(44, Math.round(44 * fontScale)), height: Math.max(44, Math.round(44 * fontScale)) },
        ]}
      >
        {importing ? (
          <ActivityIndicator size="small" color={Colors.warning} />
        ) : (
          <Ionicons
            name={needsReimport ? 'warning' : (playing ? 'pause' : icon)}
            size={20}
            color={needsReimport ? Colors.warning : (playing ? Colors.bg : Colors.textSecondary)}
          />
        )}
      </Pressable>

      <View style={styles.body}>
        <View style={styles.nameRow}>
          <Text style={[styles.name, playing && styles.nameActive, needsReimport && styles.nameMissing, { fontSize: Math.round(15 * fontScale) }]} numberOfLines={1} allowFontScaling={false}>
            {name}
          </Text>
          {playing && !needsReimport && (
            <Text style={[styles.volLabel, { fontSize: Math.round(11 * fontScale) }]} allowFontScaling={false}>
              {Math.round(volume * 100)}%
            </Text>
          )}
        </View>
        {needsReimport ? (
          <Text style={[styles.reimportHint, { fontSize: Math.round(11 * fontScale) }]} allowFontScaling={false}>
            File not found — tap to re-import
          </Text>
        ) : (
          <Slider
            style={styles.slider}
            minimumValue={0}
            maximumValue={1}
            value={volume}
            onValueChange={onVolumeChange}
            disabled={!playing}
            minimumTrackTintColor={playing ? Colors.accent : Colors.accentTrack}
            maximumTrackTintColor={Colors.accentTrack}
            thumbTintColor={playing ? Colors.accent : 'transparent'}
          />
        )}
      </View>

      {/* Optional remove button — shown when the parent passes onRemove */}
      {onRemove && (
        <Pressable onPress={onRemove} style={styles.removeBtn} hitSlop={8}>
          <Ionicons name="trash-outline" size={16} color={Colors.textMuted} />
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  tile: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.surface,
    borderRadius: Radius.lg,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: Colors.border,
    overflow: 'hidden',
  },
  tileActive: {
    backgroundColor: Colors.surface2,
    borderColor: Colors.accent + '55',
    // web shadow for glow effect
    shadowColor: Colors.accent,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.18,
    shadowRadius: 8,
    elevation: 4,
  },

  accentBar: {
    width: 3,
    alignSelf: 'stretch',
    backgroundColor: 'transparent',
  },
  accentBarActive: {
    backgroundColor: Colors.accent,
  },

  iconButton: {
    width: 44,
    height: 44,
    borderRadius: Radius.full,
    backgroundColor: Colors.surface2,
    alignItems: 'center',
    justifyContent: 'center',
    margin: 12,
    borderWidth: 1.5,
    borderColor: 'transparent',
  },
  iconButtonActive: {
    backgroundColor: Colors.accent,
    borderColor: Colors.accent + 'aa',
    shadowColor: Colors.accent,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 6,
    elevation: 3,
  },

  body: { flex: 1, paddingRight: 14, paddingVertical: 12 },

  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  name: {
    color: Colors.textSecondary,
    // fontSize applied inline via useFontScale() to update mid-session
    fontFamily: Fonts.medium,
    flex: 1,
  },
  nameActive: {
    color: Colors.textBright,
    fontFamily: Fonts.semiBold,
  },
  volLabel: {
    color: Colors.accent,
    // fontSize applied inline via useFontScale() to update mid-session
    fontFamily: Fonts.medium,
    marginLeft: 8,
  },

  slider: { width: '100%', height: 28, marginLeft: -2 },

  tileMissing: {
    opacity: 0.75,
    borderColor: Colors.warning + '88',
  },
  iconButtonMissing: {
    backgroundColor: Colors.surface2,
  },
  iconButtonImporting: {
    opacity: 0.7,
  },
  nameMissing: {
    color: Colors.textMuted,
  },
  reimportHint: {
    color: Colors.warning,
    fontFamily: Fonts.medium,
    marginTop: 2,
  },

  removeBtn: {
    paddingHorizontal: 12,
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: Colors.border,
  },
});
